import {
  Circle,
  Close,
  DoneAll,
  DoneAllOutlined,
  NotificationsActive,
  TagFaces,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Tooltip,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Fragment, useContext, useEffect, useState } from "react";
import moment from "moment";
import Config from "../../Utils/Config";
import styles from "../../Styles/mainHeader.module.css";
import { endpoints } from "../../Utils/UrlEnum";
import { WebSocketContext } from "../../Context/WebSocketContext";
import { updateData } from "../../Services/updateData";

export default function Notifications() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const clientId = localStorage.getItem("clientId");
  const websocket = useContext(WebSocketContext);

  // WebSocket event handlers setup
  useEffect(() => {
    const handleNotificationsUpdate = (data: any) => {
      setNotifications(data?.notifications || []);
      setUnreadCount(data?.unreadCount || 0);
    };

    const handleNewNotification = (data: any) => {
      refreshNotifications();
      showBrowserNotification(data.message);
    };

    refreshNotifications();
    websocket.socket.on("notifications", handleNotificationsUpdate);
    websocket.socket.on("notification:new", handleNewNotification);
    websocket.socket.emit("join", clientId);

    return () => {
      websocket.socket.off("notifications", handleNotificationsUpdate);
      websocket.socket.off("notification:new", handleNewNotification);
    };
  }, []);

  // Helper functions
  const refreshNotifications = () => {
    websocket.socket.emit("getNotificationsForClients", { clientId });
  };

  const showBrowserNotification = (message: string) => {
    if (Notification.permission === "granted") {
      new Notification("🔔" + Vocabulary.newMessage, {
        body: message,
        icon: "/sigur-la-volan-logo.png",
        tag: "app-notification",
        badge: "/sigur-la-volan-logo.png",
        requireInteraction: true,
      });
    }
  };

  // Event handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markSingleNotificationAsRead = (notificationId: string) => {
    if (!notificationId) return;
    
    updateData(`${endpoints.notifications.markAsRead}`, {
      notificationsIds: [notificationId],
    }).then((res: any) => {
      if (res) {
        refreshNotifications();
      }
    });
  };

  const markAllNotificationsAsRead = () => {
    const unreadIds = notifications
      .filter((n: any) => !n.seen)
      .map((n: any) => n._id);
    if (unreadIds.length === 0) return;

    updateData(`${endpoints.notifications.markAsRead}`, {
      notificationsIds: unreadIds,
    }).then((res: any) => {
      if (res) {
        refreshNotifications();
      }
    });
  };

  return (
    <>
      <Fab
        id="notificationsFab"
        size="small"
        className={styles.fab}
        onClick={handleClick}
      >
        <Badge
          badgeContent={unreadCount}
          max={Config.maxNotificationCount}
          classes={{ badge: styles.badge }}
        >
          <NotificationsActive />
        </Badge>
      </Fab>
      <Popover
        id={id}
        open={open}
        className="notificationsPopover"
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Card>
          <CardHeader
            subheader={Vocabulary.notifications}
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {unreadCount > 0 && (
                  <Button
                    size="small"
                    startIcon={<DoneAllOutlined />}
                    onClick={markAllNotificationsAsRead}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    {Vocabulary.markAllAsRead}
                  </Button>
                )}
                <IconButton onClick={handleClose}>
                  <Close />
                </IconButton>
              </Box>
            }
          />
          <CardContent sx={{ padding: 0 }}>
            <List>
              {notifications.length > 0 ? (
                notifications.map((notification: any) => {
                  return (
                    <Fragment key={notification._id}>
                      <Tooltip title={notification.message}>
                        <ListItem
                          className={notification.seen ? styles.notificationRead : styles.notificationNotRead}
                          onClick={() => !notification.seen && markSingleNotificationAsRead(notification._id)}
                          sx={{ cursor: !notification.seen ? "pointer" : "default" }}
                        >
                          <ListItemAvatar
                            className={notification.seen ? styles.messageRead : styles.messageNotRead}
                          >
                            {notification.seen ? <DoneAll /> : <Circle />}
                          </ListItemAvatar>
                          <ListItemText
                            className={notification.seen ? styles.messageRead : styles.messageNotRead}
                            primary={notification.message}
                            secondary={moment(notification.createdAt).format(Config.momentEUDateTimeFormat)}
                          />
                        </ListItem>
                      </Tooltip>
                      <Divider />
                    </Fragment>
                  );
                })
              ) : (
                <ListItem>
                  <ListItemText>{Vocabulary.noNotifications}</ListItemText>
                  <ListItemAvatar>
                    <TagFaces />
                  </ListItemAvatar>
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
}
